/**
 * Manages push notification subscription lifecycle
 * Handles VAPID key rotation, subscription syncing, and permission management
 */
export class PushSubscriptionManager {
  constructor(options = {}) {
    this.vapidKey = options.vapidKey
    this.endpoint = options.endpoint || '/notifications/endpoints'
    this.cooldownMs = options.cooldownMs || 3000
    
    // Internal state
    this.state = {
      syncInFlight: false,
      lastSyncAt: 0,
      serviceWorkerReady: null
    }
    
    this.storageKey = 'vapid_public_key_used_for_subscription'
    
    // Explicitly bind methods for Safari compatibility
    this.sync = this.sync.bind(this)
    this.enable = this.enable.bind(this)
    this.initialize = this.initialize.bind(this)
    this.ensureSubscription = this.ensureSubscription.bind(this)
    this.getStatus = this.getStatus.bind(this)
  }

  /**
   * Initialize the manager - call this after service worker is ready
   */
  async initialize(serviceWorkerRegistration) {
    console.debug('[PushManager] Initializing...')
    this.state.serviceWorkerReady = serviceWorkerRegistration
    
    // If already granted, sync immediately
    if (Notification.permission === 'granted') {
      await this.sync()
    } else {
      console.debug('[PushManager] Notifications permission not granted, waiting for user action')
    }
  }

  /**
   * Enable push notifications (request permission and subscribe)
   */
  async enable() {
    console.debug('[PushManager] Enabling push notifications...')
    
    try {
      const permission = await Notification.requestPermission()
      console.debug('[PushManager] Permission result:', permission)
      
      if (permission === 'granted') {
        await this.ensureSubscription()
        return true
      } else {
        console.warn('[PushManager] Permission not granted:', permission)
        return false
      }
    } catch (error) {
      console.error('[PushManager] Enable failed:', error?.name, error?.message)
      return false
    }
  }

  /**
   * Sync subscription with server (with rate limiting)
   */
  async sync() {
    if (!this.canSync()) {
      console.debug('[PushManager] Sync skipped - rate limited or in progress')
      return
    }

    this.state.syncInFlight = true
    
    try {
      const ready = this.state.serviceWorkerReady || await navigator.serviceWorker.ready
      const subscription = await ready.pushManager.getSubscription()
      
      if (subscription) {
        await this.registerSubscriptionWithServer(subscription)
        console.debug('[PushManager] Subscription synced with server')
      } else {
        console.debug('[PushManager] No subscription to sync')
      }
    } catch (error) {
      // Log but don't throw - syncing should be resilient
      console.debug('[PushManager] Sync failed (will retry later):', error?.message)
    } finally {
      this.state.lastSyncAt = Date.now()
      this.state.syncInFlight = false
    }
  }

  /**
   * Ensure valid subscription exists with current VAPID key
   */
  async ensureSubscription() {
    if (!this.vapidKey) {
      console.warn('[PushManager] No VAPID key available')
      return null
    }

    const ready = this.state.serviceWorkerReady || await navigator.serviceWorker.ready
    const storedKey = this.getStoredVapidKey()
    let subscription = await ready.pushManager.getSubscription()

    // Check if VAPID key has rotated
    if (subscription && storedKey !== this.vapidKey) {
      console.debug('[PushManager] VAPID key rotated, unsubscribing...')
      await subscription.unsubscribe()
      subscription = null
      this.clearStoredVapidKey()
    }

    // Create new subscription if needed
    if (!subscription) {
      subscription = await this.createSubscription(ready)
    }

    if (subscription) {
      await this.registerSubscriptionWithServer(subscription)
      console.debug('[PushManager] Subscription ensured and synced')
    }

    return subscription
  }

  /**
   * Create a new push subscription
   */
  async createSubscription(serviceWorkerRegistration) {
    try {
      const applicationServerKey = this.urlBase64ToUint8Array(this.vapidKey)
      const subscription = await serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      })
      
      this.setStoredVapidKey(this.vapidKey)
      console.debug('[PushManager] New subscription created')
      return subscription
    } catch (error) {
      console.error('[PushManager] Subscription creation failed:', error?.name, error?.message)
      return null
    }
  }

  /**
   * Register subscription with server
   */
  async registerSubscriptionWithServer(subscription) {
    const token = this.getMetaContent('csrf-token')
    
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token ? { 'X-CSRF-Token': token } : {})
        },
        credentials: 'same-origin',
        body: JSON.stringify(subscription.toJSON())
      })

      if (!response.ok && response.status !== 201 && response.status !== 200) {
        const text = await response.text().catch(() => '')
        throw new Error(`Subscription registration failed: ${response.status} ${text}`)
      }
    } catch (error) {
      // Network errors are expected sometimes - don't throw
      console.debug('[PushManager] Server registration failed:', error?.message)
      throw error // Re-throw so caller can handle
    }
  }

  /**
   * Check if sync is allowed (rate limiting)
   */
  canSync() {
    const now = Date.now()
    return !this.state.syncInFlight && (now - this.state.lastSyncAt) >= this.cooldownMs
  }

  /**
   * Get current subscription status
   */
  async getStatus() {
    try {
      const ready = await navigator.serviceWorker.ready
      const subscription = await ready.pushManager.getSubscription()
      
      return {
        permission: Notification.permission,
        hasSubscription: !!subscription,
        vapidKeyMatch: subscription ? this.getStoredVapidKey() === this.vapidKey : null
      }
    } catch (error) {
      console.error('[PushManager] Status check failed:', error)
      return {
        permission: 'denied',
        hasSubscription: false,
        vapidKeyMatch: false
      }
    }
  }

  // Utility methods
  getMetaContent(name) {
    const element = document.querySelector(`meta[name="${name}"]`)
    const value = element?.getAttribute('content')
    return value?.trim() || null
  }

  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const rawData = atob(base64)
    const outputArray = new Uint8Array(rawData.length)
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }

  // Storage helpers (with error handling)
  getStoredVapidKey() {
    try {
      return localStorage.getItem(this.storageKey)
    } catch (error) {
      console.warn('[PushManager] Could not read stored VAPID key:', error.message)
      return null
    }
  }

  setStoredVapidKey(key) {
    try {
      localStorage.setItem(this.storageKey, key)
    } catch (error) {
      console.warn('[PushManager] Could not store VAPID key:', error.message)
    }
  }

  clearStoredVapidKey() {
    try {
      localStorage.removeItem(this.storageKey)
    } catch (error) {
      console.warn('[PushManager] Could not clear stored VAPID key:', error.message)
    }
  }
}